import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { compose } from 'redux';
import { connect } from 'react-redux';
import { getRecoveryPlan } from '../../redux/reducers/recovery';
import { getUserSkills } from '../../redux/reducers/skills';
// components
import { Link } from 'react-router-dom';
import LadderLogo from '../LadderLogo/LadderLogo';
import Icon from '../Icon/Icon';
import Preloader from '../Preloader/Preloader';
import CollapseSection from './components/CollapseSection';
import Calendar from '../Calendar/Calendar';
import ProgressGoalLine from '../../containers/RecoveryPlan/components/ProgressGoalLine';
import RecoveryClock from '../../containers/RecoveryPlan/components/RecoveryClock';
// hoc
import onClickOutside from 'react-onclickoutside';
// utils
import daysDiff from '../../utils/daysDiff';
import { getSkillsAccess } from '../../containers/Routes/utils';
// shape
import userShape from '../../shapes/user';
// utils
import { getValuesFromGoals } from '../../containers/RecoveryPlan/utils';
import isEmpty from 'lodash.isempty';
// styles
import classnames from 'classnames/bind';
import color from 'color';
import Style from 'style-it';
import styles from './Sidebar.mod.css';

let cx = classnames.bind(styles);

class Sidebar extends Component {
  handleClickOutside = () => {
    this.props.isOpen && this.props.toggleSidebar();
  };

  componentWillReceiveProps(nextProps) {
    const {
      getRecoveryPlan,
      getUserSkills,
      shouldFetchRecovery,
      skills,
      user,
    } = nextProps;

    if (nextProps.isOpen && !this.props.isOpen) {
      const shouldFetchSkills = getSkillsAccess(user) && !skills;
      shouldFetchSkills && getUserSkills();
      shouldFetchRecovery && getRecoveryPlan();
    }
  }

  renderGoals = () => {
    const { user, goals } = this.props;
    const color = user.profile_color || '#33C370';

    const userGoals = getValuesFromGoals(goals);

    const values = Object.keys(userGoals)
      .filter(key => userGoals[key].value)
      .map(key => userGoals[key]);

    const hasValues = !isEmpty(values);

    return hasValues ? (
      <ul>
        {values.map((goal, index) => (
          <li className={cx('list-item')} key={index}>
            <span>{goal.value}</span>
            <ProgressGoalLine goal={goal} color={color} />
            <div
              className={cx('award-svg', {
                showAward: goal.award,
                'hint--bottom': goal.award,
              })}
              data-hint={goal.award}
            >
              <Icon type="award" className={cx('icon')} color={color} />
            </div>
          </li>
        ))}
      </ul>
    ) : null;
  };

  renderList = data => {
    if (isEmpty(data)) return null;
    const { plain = [], personal } = data;
    const personalList = personal ? personal.content_progress.answers_list : [];

    return (
      <ul>
        {[...plain, ...personalList].map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  };

  getBestScore = () => {
    const {
      user: { progress },
    } = this.props;
    if (!progress) {
      return;
    }
    const { best_attempt_stop, best_attempt_start, start } = progress.clocks;
    return best_attempt_stop
      ? daysDiff(best_attempt_start, best_attempt_stop)
      : daysDiff(start);
  };

  renderTriggers = () => {
    const { triggers } = this.props;
    if (isEmpty(triggers)) return null;

    return Object.keys(triggers).map(key => (
      <div className={cx('trigger-container')} key={key}>
        <h4 className={cx('trigger-title')}>{key}</h4>
        {this.renderList(triggers[key])}
      </div>
    ));
  };

  getCurrent = element => {
    return element ? element[Object.keys(element).shift()] : null;
  };

  render() {
    const {
      isOpen,
      toggleSidebar,
      user,
      risk,
      skills,
      blockers,
      breakers,
      motivators,
      satisfaction,
      isRecoveryLoading,
      isSkillsLoading,
    } = this.props;

    const userColor = user.profile_color || '#33C370';
    const { clocks } = user.progress || user.client_person.progress;

    const daysInRecovery = daysDiff(clocks.last_attempt_start || clocks.start);
    const recoveryTitle = `${daysInRecovery} ${
      daysInRecovery === 1 ? 'day' : 'days'
    } in recovery`;
    const currentRisk = this.getCurrent(risk);
    const currentSatisfaction = this.getCurrent(satisfaction);
    const bestScore = this.getBestScore();
    const isPreloaderActive = isRecoveryLoading || isSkillsLoading;
    const hasSkillAccess = getSkillsAccess(user);

    return (
      <aside className={cx('sidebar', { 'is-open': isOpen })}>
        <div
          className={cx('background')}
          style={{ backgroundColor: color(userColor).fade(0.4) }}
        />
        <div className={cx('content')}>
          <div className={cx('sidebar-header')}>
            <div className={cx('brand-box')}>
              <LadderLogo />
              <h2>
                LADDER<span>OUT</span>
              </h2>
            </div>
            <Icon
              type="close"
              className={cx('close-icon')}
              onClick={toggleSidebar}
            />
          </div>
          <div className={cx('sidebar-content')}>
            <Preloader custom isActive={isPreloaderActive} />

            <RecoveryClock recoveryTitle={recoveryTitle} recoveryTime />

            <div className={cx('calendar-container')}>
              <Calendar
                className={cx('calendar', { hide: !hasSkillAccess })}
                events={skills}
                color={userColor}
              />
            </div>

            <CollapseSection
              title="My Goals"
              placeholder="You have not add goals yet."
            >
              {this.renderGoals()}
            </CollapseSection>

            <CollapseSection
              title="My Triggers"
              placeholder="You have not add triggers yet."
            >
              {this.renderTriggers()}
            </CollapseSection>

            <CollapseSection
              title="My Blockers"
              placeholder="You don`t have blockers yet."
            >
              {this.renderList(blockers)}
            </CollapseSection>

            <CollapseSection
              title="My Breakers"
              placeholder="You don`t have breakers yet."
            >
              {this.renderList(breakers)}
            </CollapseSection>

            <CollapseSection
              title="My Motivators"
              placeholder="You don`t have motivators yet."
            >
              {this.renderList(motivators)}
            </CollapseSection>

            <div className={cx('current-values')}>
              <p className={cx({ hide: !currentSatisfaction })}>
                Current Life Satisfaction:{' '}
                <span
                  style={{ color: userColor }}
                >{`${currentSatisfaction}%`}</span>
              </p>
              <p className={cx({ hide: !currentRisk })}>
                Current Relapse Risk:{' '}
                <span style={{ color: userColor }}>{`${currentRisk}%`}</span>
              </p>
              <p>
                Best score:{' '}
                <span style={{ color: userColor }}>{`${bestScore} days`}</span>
              </p>
            </div>

            <Style>
              {`
                .link:hover { color: ${userColor}; }
              `}
            </Style>

            <div className={cx('recovery')}>
              <Style>
                {`
                .link:hover { color: ${userColor}; };
                .link .title:after { background-color: ${userColor}; }
              `}
                <Link
                  to="/recovery-plan"
                  onClick={toggleSidebar}
                  className="link"
                >
                  <Icon type="recovery" className={cx('icon')} />
                  <Style>
                    {`.title:after { background-color: ${userColor}; } `}
                    <h3 className="title">Recovery plan</h3>
                  </Style>
                </Link>
              </Style>
            </div>
          </div>
        </div>
      </aside>
    );
  }
}

Sidebar.propTypes = {
  isOpen: T.bool.isRequired,
  toggleSidebar: T.func.isRequired,
  user: userShape,
};

const enhance = compose(
  connect(
    state => ({
      user: state.auth.user,
      shouldFetchRecovery: state.recovery.shouldBeUpdated,
      goals: state.recovery.goals,
      blockers: state.recovery.blockers,
      breakers: state.recovery.breakers,
      motivators: state.recovery.motivators,
      triggers: state.recovery.triggers,
      satisfaction: state.recovery.satisfaction,
      risk: state.recovery.risk,
      skills: state.skills.userSkills,
      isRecoveryLoading: state.recovery.isLoading,
      isSkillsLoading: state.skills.isLoading,
    }),
    { getRecoveryPlan, getUserSkills }
  ),
  onClickOutside
);

export default enhance(Sidebar);



// WEBPACK FOOTER //
// ./src/components/Sidebar/Sidebar.js